/**
 * The audio system's decisions, kept free of Web Audio so they can be tested: the player car's gearbox
 * and engine speed, the ambience mix for where the listener is, footstep surfaces and cadence, impact
 * strength from velocity changes, and which traffic cars get the few engine voices.
 */

/** Engine speeds (rpm) and each gear's top speed (m/s); the car's top speed is 32 m/s. */
export const GEARBOX = { idle: 850, redline: 6800, upshift: 6100, downshift: 2600, tops: [9.5, 16, 23, 29, 34], shiftSeconds: 0.22 };

/**
 * An automatic gearbox: engine speed from road speed in the current gear, shifting up near the redline
 * and down when the revs fall. The clutch slips in first gear (revs rise with the throttle from a
 * standstill). While a shift is in progress `load` drops to 0: the engine note dips, as it does in a
 * real car.
 */
export class Gearbox {
  gear = 0;
  rpm: number = GEARBOX.idle;
  /** 0 … 1: how hard the engine is working (throttle, cut during shifts). */
  load = 0;
  private shifting = 0;

  update(dt: number, speed: number, throttle: number): void {
    const v = Math.abs(speed);
    const G = GEARBOX;
    const inGear = (g: number) => G.idle + (v / G.tops[g]) * (G.redline - G.idle);
    let target = inGear(this.gear);
    if (this.shifting <= 0) {
      if (target > G.upshift && throttle > 0 && this.gear < G.tops.length - 1) {
        this.gear++;
        this.shifting = G.shiftSeconds;
      } else if (this.gear > 0 && target < G.downshift) {
        this.gear--;
        this.shifting = G.shiftSeconds * 0.6;
      }
      target = inGear(this.gear);
    }
    this.shifting -= dt;
    const t = Math.max(0, throttle);
    // Clutch slip in first (and reverse): revs follow the throttle, not just the wheels.
    if (this.gear === 0) target = Math.max(target, G.idle + t * 2600 * Math.max(0, 1 - v / 6));
    target = Math.min(G.redline, Math.max(G.idle, target));
    // Revs rise quickly and fall a little slower; during a shift they sag toward the new gear.
    const rate = target > this.rpm ? 9 : 6;
    this.rpm += (target - this.rpm) * (1 - Math.exp(-dt * rate));
    const wantLoad = this.shifting > 0 ? 0 : Math.max(t, speed < -0.3 && throttle < 0 ? -throttle : 0);
    this.load += (wantLoad - this.load) * (1 - Math.exp(-dt * 12));
  }

  /** 0 at idle … 1 at the redline. */
  get rev(): number {
    return (this.rpm - GEARBOX.idle) / (GEARBOX.redline - GEARBOX.idle);
  }
}

export interface ListenerEnv {
  /** Metres above the ground (terrain or roads) below the listener. */
  height: number;
  indoors: boolean;
  /** Trees within 40 m, 0 … 1 (1 = a park). */
  trees: number;
  /** Traffic cars within 80 m. */
  cars: number;
  /** The listener's speed (m/s): the fly camera's wind. */
  speed: number;
}

export interface AmbienceMix {
  city: number;
  air: number;
  birds: number;
  room: number;
  /** Rushing wind from moving fast (fly mode). */
  wind: number;
  /** Low-pass cutoff (Hz) on everything outdoors: walls muffle the street inside. */
  outdoorCutoff: number;
  /** Reverb sends: the street's slap-back, a room's short tail. */
  street: number;
  hall: number;
}

/**
 * Gains (linear) of the ambience beds for where the listener is. The street bed fades with height as
 * the rushing air of the skyline takes over; birds need trees and quiet; indoors everything outside is
 * muffled and the room tone comes up.
 */
export function ambienceMix(e: ListenerEnv): AmbienceMix {
  const h = Math.max(0, e.height);
  const busy = Math.min(1, e.cars / 12);
  const ground = Math.exp(-h / 90);
  const city = (0.55 + 0.45 * busy) * ground;
  const air = Math.min(1, 0.12 + h / 140) * (0.6 + 0.4 * (1 - ground));
  const birds = e.trees * Math.exp(-h / 25) * (1 - 0.5 * busy);
  const wind = Math.min(1, Math.pow(Math.max(0, e.speed - 4) / 60, 1.4));
  if (e.indoors) return { city: city * 0.8, air: air * 0.5, birds: birds * 0.4, room: 0.9, wind: 0, outdoorCutoff: 650, street: 0, hall: 1 };
  return { city, air, birds, room: 0, wind, outdoorCutoff: 20000, street: ground, hall: 0 };
}

export type StepSurface = 'concrete' | 'grass' | 'wood' | 'carpet' | 'snow';

/** Terrain above this height (m) is snowy (terrain/tileMesh.ts shades it white from 300 m). */
export const SNOW_LINE = 320;

/** The footstep sound for what the walker stands on (a collider tag, physics/physics.ts). */
export function stepSurface(tag: string | undefined, y: number, floorHash = 0): StepSurface {
  switch (tag) {
    case 'terrain':
      return y > SNOW_LINE ? 'snow' : 'grass';
    case 'interior':
      // Rooms are wood or carpeted; pick per floor so a floor sounds the same throughout.
      return floorHash % 3 === 0 ? 'carpet' : 'wood';
    case 'furniture':
      return 'wood';
    default:
      return 'concrete';
  }
}

/** Counts distance walked into footsteps: one every stride, plus a landing thump. */
export class Strides {
  private dist = 0;
  private air = 0;

  /** Distance (m) moved this frame; returns 'step', 'land' or null. */
  update(dt: number, moved: number, grounded: boolean, running: boolean): 'step' | 'land' | null {
    if (!grounded) {
      this.air += dt;
      return null;
    }
    const landed = this.air > 0.35;
    this.air = 0;
    if (landed) {
      this.dist = 0;
      return 'land';
    }
    this.dist += moved;
    const stride = running ? 1.35 : 0.78;
    if (this.dist >= stride) {
      this.dist -= stride;
      if (this.dist > stride) this.dist = 0;
      return 'step';
    }
    return null;
  }
}

/**
 * How hard a body was hit (m/s): the change in its velocity over one physics step beyond what gravity,
 * the engine and the brakes can do (collisions change it by metres per second at once).
 */
export function impactSpeed(prev: [number, number, number], cur: [number, number, number], h: number): number {
  const dx = cur[0] - prev[0];
  const dy = cur[1] - prev[1] + 9.81 * h;
  const dz = cur[2] - prev[2];
  // Suspension and kerbs push up a little every step; count vertical change at half weight.
  const d = Math.hypot(dx, dz, dy * 0.5);
  return Math.max(0, d - 0.35);
}

/**
 * Hand the voices (`current`: the car id per voice, null = free) to the nearest candidates (id,
 * distance). Each of the nearest `slots` cars gets a free voice, or takes the voice of the farthest
 * voiced car when that one is clearly farther (by 20 % + 2 m), so voices don't flick between cars at
 * similar distances. Cars no longer among the candidates lose their voice.
 */
export function assignVoices(current: (number | null)[], candidates: { id: number; d: number }[]): (number | null)[] {
  const dist = new Map(candidates.map((c) => [c.id, c.d]));
  const out = current.map((id) => (id !== null && dist.has(id) ? id : null));
  const sorted = [...candidates].sort((a, b) => a.d - b.d).slice(0, out.length);
  for (const c of sorted) {
    if (out.includes(c.id)) continue;
    let slot = out.indexOf(null);
    if (slot < 0) {
      let far = -1;
      out.forEach((id, i) => {
        if (far < 0 || dist.get(id!)! > dist.get(out[far]!)!) far = i;
      });
      if (dist.get(out[far]!)! > c.d * 1.2 + 2) slot = far;
    }
    if (slot >= 0) out[slot] = c.id;
  }
  return out;
}

/** Doppler factor for a source closing on the listener at `closing` m/s (negative when receding). */
export function doppler(closing: number): number {
  const c = 343;
  return c / (c - Math.max(-60, Math.min(60, closing)));
}
